<script setup>
import {
  ref,
  reactive,
  toRefs,
  useTemplateRef,
  computed,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useVuelidate } from '@vuelidate/core';
import validations, {
  getLabelTitleErrorMessage,
} from 'dashboard/routes/dashboard/settings/labels/validations';
import { PRESET_LABEL_COLORS } from 'dashboard/helper/labelColor';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'conversation',
  },
  action: {
    type: String,
    default: 'assign',
    validator: value => ['assign', 'remove'].includes(value),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  appliedLabels: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['assign', 'remove']);

const { t } = useI18n();
const store = useStore();
const contactLabelsStore = useContactLabelsStore();

const conversationLabels = useMapGetter('labels/getLabels');
const isTypeContact = computed(() => props.type === 'contact');
const labels = computed(() =>
  isTypeContact.value ? contactLabelsStore.getLabels : conversationLabels.value
);

onMounted(() => {
  if (isTypeContact.value) contactLabelsStore.fetchAll();
});

const containerRef = useTemplateRef('containerRef');
const [showDropdown, toggleDropdown] = useToggle(false);
const selectedLabels = ref([]);
const isCreating = ref(false);
// 'assign' | 'manage'
const view = ref('assign');
// { id, color } being edited inline
const editingColor = ref({});
// id of label whose color picker is open
const colorPickerOpen = ref(null);

// Same title validation rules used in Settings > Labels (required, min
// length 2, no spaces), so creating a label here follows the same rules
// and surfaces the same error message instead of just disabling silently.
const titleState = reactive({ title: '' });
const v$ = useVuelidate({ title: validations.title }, titleState);
const { title: searchQuery } = toRefs(titleState);
const titleErrorMessage = computed(() =>
  t(getLabelTitleErrorMessage(v$.value))
);

const isRemoveAction = computed(() => props.action === 'remove');

const canCreateLabel = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q || isRemoveAction.value || v$.value.title.$invalid) return false;
  return !labels.value.some(l => l.title.toLowerCase() === q);
});

const buttonLabel = computed(() => {
  if (!isTypeContact.value) {
    return isRemoveAction.value
      ? t('BULK_ACTION.BUTTONS.REMOVE_LABEL')
      : t('BULK_ACTION.BUTTONS.ADD_LABEL');
  }
  return isRemoveAction.value
    ? t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS')
    : t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS');
});

const tooltipLabel = computed(() =>
  isRemoveAction.value
    ? t('BULK_ACTION.LABELS.REMOVE_LABELS')
    : t('BULK_ACTION.LABELS.ASSIGN_LABELS')
);

const confirmLabel = computed(() =>
  isRemoveAction.value
    ? t('BULK_ACTION.LABELS.REMOVE_SELECTED_LABELS')
    : t('BULK_ACTION.LABELS.ASSIGN_SELECTED_LABELS')
);

const isLabelSelected = labelTitle => selectedLabels.value.includes(labelTitle);

const visibleLabels = computed(() => {
  if (!isRemoveAction.value || props.appliedLabels === null)
    return labels.value;
  const applied = new Set(props.appliedLabels);
  return labels.value.filter(label => applied.has(label.title));
});

const labelMenuItems = computed(() =>
  visibleLabels.value.map(label => ({
    action: 'select',
    value: label.title,
    label: label.title,
    color: label.color,
    id: label.id,
    isSelected: isLabelSelected(label.title),
  }))
);

const toggleLabelSelection = labelTitle => {
  const index = selectedLabels.value.indexOf(labelTitle);
  if (index > -1) selectedLabels.value.splice(index, 1);
  else selectedLabels.value.push(labelTitle);
};

const handleApply = () => {
  if (selectedLabels.value.length > 0) {
    if (isRemoveAction.value) emit('remove', selectedLabels.value);
    else emit('assign', selectedLabels.value);
    toggleDropdown(false);
    selectedLabels.value = [];
  }
};

const handleDismiss = () => {
  selectedLabels.value = [];
  searchQuery.value = '';
  v$.value.$reset();
  view.value = 'assign';
  editingColor.value = {};
  colorPickerOpen.value = null;
  toggleDropdown(false);
};

const handleSearch = value => {
  searchQuery.value = value;
  v$.value.title.$touch();
};

const handleCreateLabel = async () => {
  await v$.value.title.$validate();
  if (v$.value.title.$invalid || isCreating.value) return;

  const title = searchQuery.value.trim();
  isCreating.value = true;
  try {
    if (isTypeContact.value) {
      await contactLabelsStore.create({ title });
    } else {
      await store.dispatch('labels/create', {
        title,
        color: '#6B7280',
      });
    }
    toggleLabelSelection(title);
    searchQuery.value = '';
    v$.value.$reset();
  } finally {
    isCreating.value = false;
  }
};

const handleEnter = () => {
  if (canCreateLabel.value) handleCreateLabel();
};

const handleDeleteLabel = async id => {
  if (isTypeContact.value) {
    await contactLabelsStore.delete(id);
  } else {
    await store.dispatch('labels/delete', id);
  }
  selectedLabels.value = selectedLabels.value.filter(
    title => !labels.value.find(l => l.id === id && l.title === title)
  );
};

const handleColorChange = async (label, color) => {
  editingColor.value = { ...editingColor.value, [label.id]: color };
  if (isTypeContact.value) {
    await contactLabelsStore.update(label.id, {
      color,
      title: label.title,
    });
  } else {
    await store.dispatch('labels/update', {
      id: label.id,
      color,
      title: label.title,
    });
  }
};

const selectPresetColor = async (label, color) => {
  colorPickerOpen.value = null;
  await handleColorChange(label, color);
};
</script>

<template>
  <div ref="containerRef" class="relative">
    <NextButton
      v-tooltip="tooltipLabel"
      :label="buttonLabel"
      :icon="isRemoveAction ? 'i-woot-tag-remove' : 'i-lucide-tag'"
      slate
      size="sm"
      ghost
      :class="{
        'bg-n-alpha-2': showDropdown,
        '[&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline w-fit !text-n-blue-11 [&>span]:!text-n-blue-11 !px-2':
          isTypeContact,
        '[&>span:nth-child(2)]:hidden lg:[&>span:nth-child(2)]:inline !px-2':
          !isTypeContact,
      }"
      :disabled="disabled || isLoading"
      :is-loading="isLoading"
      @click="toggleDropdown()"
    />
    <Transition
      :enter-active-class="
        !isTypeContact
          ? 'transition-all duration-150 ease-out origin-bottom'
          : 'transition-all duration-150 ease-out origin-top'
      "
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      :leave-active-class="
        !isTypeContact
          ? 'transition-all duration-100 ease-in origin-bottom'
          : 'transition-all duration-100 ease-in origin-top'
      "
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <!-- Assign view -->
      <DropdownMenu
        v-if="showDropdown && view === 'assign'"
        v-on-click-outside="[handleDismiss, { ignore: [containerRef] }]"
        :menu-items="labelMenuItems"
        show-search
        :search-placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        class="w-64 max-h-80"
        :class="{
          'ltr:right-0 rtl:left-0 bottom-10': !isTypeContact,
          'ltr:right-0 rtl:left-0 mb-1 top-10': isTypeContact,
        }"
        @action="item => toggleLabelSelection(item.value)"
        @search="handleSearch"
        @keydown.enter.capture="handleEnter"
      >
        <template #thumbnail="{ item }">
          <span
            class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
            :style="{ backgroundColor: item.color }"
          />
        </template>

        <template #trailing-icon="{ item }">
          <Icon
            v-if="isLabelSelected(item.value)"
            icon="i-lucide-check"
            class="size-4 text-n-blue-11 flex-shrink-0"
          />
        </template>

        <template #footer>
          <div
            class="sticky bottom-0 rounded-b-md px-2 py-2 z-20 bg-n-alpha-3 backdrop-blur-[4px] flex flex-col gap-1.5"
          >
            <button
              v-if="canCreateLabel"
              type="button"
              class="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-left"
              :disabled="isCreating"
              @click="handleCreateLabel"
            >
              <Icon
                icon="i-lucide-corner-down-left"
                class="size-3.5 shrink-0 text-n-slate-11"
              />
              <span>{{
                t('BULK_ACTION.LABELS.CREATE_LABEL', {
                  title: searchQuery.trim(),
                })
              }}</span>
            </button>
            <span
              v-else-if="!isRemoveAction && v$.title.$error"
              class="text-xs text-n-ruby-11 px-2"
            >
              {{ titleErrorMessage }}
            </span>
            <NextButton
              sm
              class="w-full [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
              :label="confirmLabel"
              :disabled="!selectedLabels.length"
              @click="handleApply"
            />
          </div>
        </template>
      </DropdownMenu>

      <!-- Manage view -->
      <div
        v-else-if="showDropdown && view === 'manage'"
        v-on-click-outside="[handleDismiss, { ignore: [containerRef] }]"
        class="bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container absolute rounded-xl z-50 shadow-lg w-64 max-h-80 flex flex-col overflow-hidden"
        :class="{
          'ltr:right-0 rtl:left-0 bottom-10': !isTypeContact,
          'ltr:right-0 rtl:left-0 mb-1 top-10': isTypeContact,
        }"
      >
        <!-- Header -->
        <div class="flex items-center gap-1 px-2 pt-2 pb-1 shrink-0">
          <button
            type="button"
            class="p-1 rounded-lg hover:bg-n-alpha-1 text-n-slate-11 transition-colors"
            @click="view = 'assign'"
          >
            <Icon icon="i-lucide-arrow-left" class="size-3.5" />
          </button>
          <span
            class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
          >
            {{ t('BULK_ACTION.LABELS.MANAGE_LABELS') }}
          </span>
        </div>
        <!-- Label list -->
        <div class="flex-1 overflow-y-auto px-2 pb-2 flex flex-col gap-0.5">
          <div
            v-for="label in labels"
            :key="label.id"
            class="flex flex-col rounded-lg hover:bg-n-alpha-1 group"
          >
            <div class="flex items-center gap-2 px-2 py-1.5">
              <!-- Color swatch button -->
              <button
                type="button"
                class="shrink-0 rounded-md h-4 w-4 border border-solid border-n-weak ring-offset-1 group-hover:ring-1 ring-n-blue-8 transition-shadow"
                :style="{
                  backgroundColor: editingColor[label.id] || label.color,
                }"
                @click.stop="
                  colorPickerOpen =
                    colorPickerOpen === label.id ? null : label.id
                "
              />
              <span class="flex-1 text-sm text-n-slate-12 truncate">{{
                label.title
              }}</span>
              <button
                type="button"
                class="opacity-0 group-hover:opacity-100 p-0.5 rounded text-n-ruby-9 hover:bg-n-ruby-3 transition-all"
                @click="handleDeleteLabel(label.id)"
              >
                <Icon icon="i-lucide-trash-2" class="size-3.5" />
              </button>
            </div>
            <!-- Color palette (shown when open) -->
            <div
              v-if="colorPickerOpen === label.id"
              class="px-2 pb-2 flex flex-col gap-2"
              @click.stop
            >
              <!-- Preset swatches -->
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="preset in PRESET_LABEL_COLORS"
                  :key="preset"
                  type="button"
                  class="h-5 w-5 rounded-md border border-solid border-n-weak hover:scale-110 transition-transform"
                  :style="{ backgroundColor: preset }"
                  :class="{
                    'ring-2 ring-offset-1 ring-n-blue-8':
                      (editingColor[label.id] || label.color) === preset,
                  }"
                  @click="selectPresetColor(label, preset)"
                />
              </div>
              <!-- Custom color input -->
              <label class="flex items-center gap-2 cursor-pointer">
                <span class="text-xs text-n-slate-11">{{
                  t('BULK_ACTION.LABELS.CUSTOM_COLOR')
                }}</span>
                <input
                  type="color"
                  class="h-5 w-8 rounded cursor-pointer border border-n-weak bg-transparent"
                  :value="editingColor[label.id] || label.color"
                  @input="e => handleColorChange(label, e.target.value)"
                />
              </label>
            </div>
          </div>
          <div
            v-if="!labels.length"
            class="text-sm text-n-slate-11 px-2 py-1.5"
          >
            {{ t('DROPDOWN_MENU.EMPTY_STATE') }}
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
